import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ForwardedRef, MutableRefObject, ReactElement } from 'react';
import { OrbitControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import {
  createViewportTweenController,
  type RuntimeOrbitControlsLike,
} from '../animation/viewportTween';
import type {
  Graph3DData,
  Graph3DBounds,
  Graph3DVector3,
  Graph3DPositionedEdge,
  Graph3DPositionedNode,
} from '../core/graph.types';
import type { Graph3DLayoutConfig } from '../core/layout.types';
import type {
  Graph3DEventHandlers,
  Graph3DEdgeRendererRegistry,
  Graph3DFocusNodeOptions,
  Graph3DFitViewOptions,
  Graph3DHandle,
  Graph3DInteractionConfig,
  Graph3DNodeRendererRegistry,
  Graph3DRuntimeViewportFollowConfig,
  Graph3DSetViewportOptions,
  Graph3DViewportState,
} from '../core/renderer.types';
import type {
  Graph3DRuntime,
  Graph3DRuntimeStatus,
} from '../core/runtime.types';
import type { Graph3DTheme } from '../core/theme.types';
import { calculateBounds } from '../layout/bounds';
import { resolveGraphLayout } from '../layout/resolveGraphLayout';
import {
  defaultEdgeRenderers,
  defaultNodeRenderers,
} from '../renderers/defaultRenderers';
import { projectWorldToNdc } from '../utils/coordTransforms';
import { AutoFitCamera } from './AutoFitCamera';
import { NodeDetailPanel3D } from './NodeDetailPanel3D';
import type { PerspectiveCamera } from 'three';

const HOVER_DETAIL_PANEL_HIDE_DELAY = 650;
const RUNTIME_FOCUS_PADDING_NDC = 0.62;
const RUNTIME_FOCUS_MIN_PAN_NDC = 0.04;
const DEFAULT_FIT_VIEW_PADDING = 88;
const DEFAULT_FIT_VIEW_DURATION = 680;
const DEFAULT_FOCUS_NODE_DURATION = 320;
const USER_VIEWPORT_GUARD_MS = 900;
const DEFAULT_RUNTIME_VIEWPORT_FOLLOW_NODE_TYPES = ['process'];
const DEFAULT_RUNTIME_VIEWPORT_FOLLOW_STATUSES: Graph3DRuntimeStatus[] = [
  'running',
];
const GRAPH3D_SCENE_PERF_PREFIX = '[Graph3D scene perf]';
const GRAPH3D_SLOW_FRAME_THRESHOLD_MS = 34;
const GRAPH3D_SLOW_FRAME_LOG_INTERVAL_MS = 180;

function shouldLogGraph3DPerf() {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    (window as Window & { __GRAPH3D_PERF_DEBUG__?: boolean })
      .__GRAPH3D_PERF_DEBUG__ === true
  );
}

function getGraph3DPerfContext() {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (
    window as Window & { __GRAPH3D_PERF_CONTEXT__?: Record<string, unknown> }
  ).__GRAPH3D_PERF_CONTEXT__;
}

interface RuntimeViewportTrackerProps<TNodeData = unknown> {
  nodes: Graph3DPositionedNode<TNodeData>[];
  runtime?: Graph3DRuntime;
  enabled?: boolean;
  nodeTypes?: string[];
  statuses?: Graph3DRuntimeStatus[];
  focusPadding?: number;
  focusDuration?: number;
  suppressAutoFocusUntil?: number;
}

function RuntimeViewportTracker<TNodeData = unknown>({
  nodes,
  runtime,
  onFocusNode,
  enabled = true,
  nodeTypes,
  statuses,
  focusPadding,
  focusDuration,
  suppressAutoFocusUntil = 0,
}: RuntimeViewportTrackerProps<TNodeData> & {
  onFocusNode: (nodeId: string, options?: Graph3DFocusNodeOptions) => void;
}) {
  const previousRunningNodeIdsRef = useRef<Set<string>>(new Set());
  const centeredNodeIdsRef = useRef<Set<string>>(new Set());
  const followedNodeTypes = nodeTypes?.length
    ? nodeTypes
    : DEFAULT_RUNTIME_VIEWPORT_FOLLOW_NODE_TYPES;
  const followedStatuses = statuses?.length
    ? statuses
    : DEFAULT_RUNTIME_VIEWPORT_FOLLOW_STATUSES;

  useEffect(() => {
    if (!enabled) {
      previousRunningNodeIdsRef.current.clear();
      centeredNodeIdsRef.current.clear();
      return;
    }

    const currentRunningNodes = nodes.filter(node => {
      if (!followedNodeTypes.includes(node.type)) {
        return false;
      }
      const nodeRuntime = runtime?.nodes?.[node.id];
      if (
        nodeRuntime?.status &&
        followedStatuses.includes(nodeRuntime.status)
      ) {
        return true;
      }
      return (
        nodeRuntime?.active === true && followedStatuses.includes('active')
      );
    });
    const previousRunningNodeIds = previousRunningNodeIdsRef.current;
    const nextRunningNode =
      currentRunningNodes.find(node => !previousRunningNodeIds.has(node.id)) ??
      currentRunningNodes.find(
        node => !centeredNodeIdsRef.current.has(node.id)
      ) ??
      null;
    const nextRunningNodeId = nextRunningNode?.id ?? null;
    previousRunningNodeIdsRef.current = new Set(
      currentRunningNodes.map(node => node.id)
    );

    if (!nextRunningNodeId) {
      return;
    }

    if (centeredNodeIdsRef.current.has(nextRunningNodeId)) {
      return;
    }

    if (Date.now() < suppressAutoFocusUntil) {
      return;
    }

    onFocusNode(nextRunningNodeId, {
      padding: focusPadding,
      duration: focusDuration,
    });
    centeredNodeIdsRef.current.add(nextRunningNodeId);
  }, [
    enabled,
    focusDuration,
    focusPadding,
    followedNodeTypes,
    followedStatuses,
    nodes,
    onFocusNode,
    runtime,
    suppressAutoFocusUntil,
  ]);

  useEffect(() => {
    centeredNodeIdsRef.current.forEach(nodeId => {
      const nodeRuntime = runtime?.nodes?.[nodeId];
      if (
        (nodeRuntime?.status &&
          followedStatuses.includes(nodeRuntime.status)) ||
        (nodeRuntime?.active === true && followedStatuses.includes('active'))
      ) {
        return;
      }
      centeredNodeIdsRef.current.delete(nodeId);
    });
  }, [followedStatuses, runtime]);

  return null;
}

function resolveRuntimeViewportFollowConfig(
  interactions?: Graph3DInteractionConfig
): Graph3DRuntimeViewportFollowConfig {
  const config = interactions?.runtimeViewportFollow;

  if (config === false) {
    return { enabled: false };
  }

  if (config === true) {
    return { enabled: true };
  }

  if (config) {
    return {
      enabled: config.enabled ?? true,
      nodeTypes: config.nodeTypes,
      statuses: config.statuses,
      padding: config.padding,
      duration: config.duration,
    };
  }

  return {
    enabled: interactions?.enableRuntimeAutoFocus !== false,
  };
}

function resolveRuntimeOverflow(value: number, padding: number) {
  if (value < -padding) {
    return value + padding;
  }
  if (value > padding) {
    return value - padding;
  }
  return 0;
}

function buildFocusNodeTarget(
  camera: PerspectiveCamera,
  controls: RuntimeOrbitControlsLike,
  worldPosition: Graph3DVector3,
  padding = RUNTIME_FOCUS_PADDING_NDC
) {
  const safePadding = Math.min(Math.max(padding, 0.12), 0.92);
  const projected = projectWorldToNdc(worldPosition, camera);
  if (projected.z < -1 || projected.z > 1) {
    return {
      x: worldPosition.x,
      y: worldPosition.y,
      z: controls.target.z,
    };
  }

  const overflowX = resolveRuntimeOverflow(projected.x, safePadding);
  const overflowY = resolveRuntimeOverflow(projected.y, safePadding);
  if (
    Math.abs(overflowX) < RUNTIME_FOCUS_MIN_PAN_NDC &&
    Math.abs(overflowY) < RUNTIME_FOCUS_MIN_PAN_NDC
  ) {
    return null;
  }

  return buildRuntimePanTarget(camera, controls, worldPosition, {
    overflowX,
    overflowY,
  });
}

function buildRuntimePanTarget(
  camera: PerspectiveCamera,
  controls: RuntimeOrbitControlsLike,
  worldPosition: Graph3DVector3,
  overflow?: {
    overflowX: number;
    overflowY: number;
  }
) {
  const target = controls.target.clone();
  const distance = camera.position.distanceTo(target);
  const verticalHalfSpan =
    Math.tan((camera.fov * Math.PI) / 360) * Math.max(distance, 1);
  const horizontalHalfSpan = verticalHalfSpan * camera.aspect;
  const nextTarget = target.clone();

  if (overflow) {
    nextTarget.x += overflow.overflowX * horizontalHalfSpan;
    nextTarget.y += overflow.overflowY * verticalHalfSpan;
  } else {
    nextTarget.x = worldPosition.x;
    nextTarget.y = worldPosition.y;
  }

  return {
    x: nextTarget.x,
    y: nextTarget.y,
    z: nextTarget.z,
  };
}

function SceneViewportController({
  bounds,
  targetCenter,
  nodes,
  centerOffset,
  viewportTweenControllerRef,
  fitViewRequest,
  viewportRequest,
  focusNodeRequest,
  onFitViewHandled,
  onViewportHandled,
  onFocusNodeHandled,
  onViewportSettled,
}: {
  bounds: Graph3DBounds;
  targetCenter: Graph3DVector3;
  nodes: Graph3DPositionedNode[];
  centerOffset: readonly [number, number, number];
  viewportTweenControllerRef: MutableRefObject<ReturnType<
    typeof createViewportTweenController
  > | null>;
  fitViewRequest: {
    token: number;
    options?: Graph3DFitViewOptions;
  } | null;
  viewportRequest: {
    token: number;
    viewport: Graph3DViewportState;
    options?: Graph3DSetViewportOptions;
  } | null;
  focusNodeRequest: {
    token: number;
    nodeId: string;
    options?: Graph3DFocusNodeOptions;
  } | null;
  onFitViewHandled: () => void;
  onViewportHandled: () => void;
  onFocusNodeHandled: () => void;
  onViewportSettled?: () => void;
}) {
  const camera = useThree(state => state.camera) as PerspectiveCamera;
  const controls = useThree(state => state.controls) as unknown as
    | RuntimeOrbitControlsLike
    | undefined;
  const viewportSettledTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const scheduleViewportSettled = useCallback(
    (duration: number) => {
      if (viewportSettledTimerRef.current) {
        clearTimeout(viewportSettledTimerRef.current);
        viewportSettledTimerRef.current = null;
      }
      viewportSettledTimerRef.current = setTimeout(
        () => {
          viewportSettledTimerRef.current = null;
          onViewportSettled?.();
        },
        Math.max(0, duration)
      );
    },
    [onViewportSettled]
  );

  useEffect(
    () => () => {
      if (viewportSettledTimerRef.current) {
        clearTimeout(viewportSettledTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!fitViewRequest || !controls) {
      return;
    }
    const duration =
      fitViewRequest.options?.duration ?? DEFAULT_FIT_VIEW_DURATION;
    viewportTweenControllerRef.current?.animateFitView({
      camera,
      controls,
      bounds,
      targetCenter,
      padding: fitViewRequest.options?.padding ?? DEFAULT_FIT_VIEW_PADDING,
      duration,
      preserveViewDirection:
        fitViewRequest.options?.preserveViewDirection === true,
    });
    scheduleViewportSettled(duration);
    onFitViewHandled();
  }, [
    bounds,
    camera,
    targetCenter,
    controls,
    fitViewRequest,
    onFitViewHandled,
    scheduleViewportSettled,
    viewportTweenControllerRef,
  ]);

  useEffect(() => {
    if (!viewportRequest || !controls) {
      return;
    }

    const duration =
      viewportRequest.options?.duration ?? DEFAULT_FOCUS_NODE_DURATION;
    viewportTweenControllerRef.current?.animateViewport({
      camera,
      controls,
      viewport: viewportRequest.viewport,
      duration,
    });
    scheduleViewportSettled(duration);
    onViewportHandled();
  }, [
    camera,
    controls,
    onViewportHandled,
    scheduleViewportSettled,
    viewportRequest,
    viewportTweenControllerRef,
  ]);

  useEffect(() => {
    if (!focusNodeRequest || !controls) {
      return;
    }

    const targetNode = nodes.find(node => node.id === focusNodeRequest.nodeId);
    if (!targetNode) {
      onFocusNodeHandled();
      return;
    }

    // 组件对外暴露 focusNode 时，统一转换为场景内的世界坐标并复用同一套 tween。
    const worldPosition = {
      x: targetNode.resolvedPosition.x + centerOffset[0],
      y: targetNode.resolvedPosition.y + centerOffset[1],
      z: targetNode.resolvedPosition.z + centerOffset[2],
    };
    const nextTarget = buildFocusNodeTarget(
      camera,
      controls,
      worldPosition,
      focusNodeRequest.options?.padding
    );

    if (nextTarget) {
      const duration =
        focusNodeRequest.options?.duration ?? DEFAULT_FOCUS_NODE_DURATION;
      viewportTweenControllerRef.current?.animatePan({
        camera,
        controls,
        nextTarget,
        duration,
      });
      scheduleViewportSettled(duration);
    }

    onFocusNodeHandled();
  }, [
    camera,
    centerOffset,
    controls,
    focusNodeRequest,
    nodes,
    onFocusNodeHandled,
    scheduleViewportSettled,
    viewportTweenControllerRef,
  ]);

  return null;
}

interface Graph3DSceneProps<TNodeData = unknown, TEdgeData = unknown> {
  data: Graph3DData<TNodeData, TEdgeData>;
  runtime?: Graph3DRuntime;
  layout?: Graph3DLayoutConfig;
  theme: Graph3DTheme;
  interactions?: Graph3DInteractionConfig;
  nodeRenderers?: Graph3DNodeRendererRegistry<TNodeData>;
  edgeRenderers?: Graph3DEdgeRendererRegistry<TEdgeData, TNodeData>;
  events?: Graph3DEventHandlers<TNodeData, TEdgeData>;
}

const Graph3DScene = forwardRef(function Graph3DSceneInner<
  TNodeData = unknown,
  TEdgeData = unknown,
>(
  {
    data,
    runtime,
    layout,
    theme,
    interactions,
    nodeRenderers,
    edgeRenderers,
    events,
  }: Graph3DSceneProps<TNodeData, TEdgeData>,
  ref: ForwardedRef<Graph3DHandle>
) {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const [nodePositionOverrides, setNodePositionOverrides] = useState<
    Record<string, Graph3DVector3>
  >({});
  const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
  const [fitViewRequest, setFitViewRequest] = useState<{
    token: number;
    options?: Graph3DFitViewOptions;
  } | null>(null);
  const [viewportRequest, setViewportRequest] = useState<{
    token: number;
    viewport: Graph3DViewportState;
    options?: Graph3DSetViewportOptions;
  } | null>(null);
  const [focusNodeRequest, setFocusNodeRequest] = useState<{
    token: number;
    nodeId: string;
    options?: Graph3DFocusNodeOptions;
  } | null>(null);
  const detailHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoveredNodeIdRef = useRef<string | null>(null);
  const detailNodeIdRef = useRef<string | null>(null);
  const detailPanelHoveredNodeIdRef = useRef<string | null>(null);
  const fitViewTokenRef = useRef(0);
  const viewportTokenRef = useRef(0);
  const focusNodeTokenRef = useRef(0);
  const userViewportInteractionUntilRef = useRef(0);
  const [userViewportInteractionUntil, setUserViewportInteractionUntil] =
    useState(0);
  const camera = useThree(state => state.camera) as PerspectiveCamera;
  const controls = useThree(state => state.controls) as unknown as
    | RuntimeOrbitControlsLike
    | undefined;
  const resolved = useMemo(
    () => resolveGraphLayout(data, layout),
    [data, layout]
  );
  const positionedResolved = useMemo(() => {
    const nodes = resolved.nodes.map(node => {
      const position = nodePositionOverrides[node.id];
      return position
        ? {
            ...node,
            resolvedPosition: position,
          }
        : node;
    });
    const nodeById = new Map(nodes.map(node => [node.id, node]));
    return {
      ...resolved,
      nodes,
      edges: resolved.edges.map(edge => ({
        ...edge,
        sourceNode: nodeById.get(edge.source) ?? edge.sourceNode,
        targetNode: nodeById.get(edge.target) ?? edge.targetNode,
      })),
    };
  }, [nodePositionOverrides, resolved]);
  const mergedNodeRenderers = useMemo(
    () =>
      ({
        ...defaultNodeRenderers,
        ...nodeRenderers,
      }) as Graph3DNodeRendererRegistry<TNodeData>,
    [nodeRenderers]
  );
  const mergedEdgeRenderers = useMemo(
    () =>
      ({
        ...defaultEdgeRenderers,
        ...edgeRenderers,
      }) as Graph3DEdgeRendererRegistry<TEdgeData, TNodeData>,
    [edgeRenderers]
  );
  const centerOffset =
    layout?.center === false
      ? ([0, 0, 0] as const)
      : ([
          -resolved.bounds.center.x,
          -resolved.bounds.center.y,
          -resolved.bounds.center.z,
        ] as const);
  const viewportTweenControllerRef = useRef<ReturnType<
    typeof createViewportTweenController
  > | null>(null);
  const runtimeViewportFollow = useMemo(
    () => resolveRuntimeViewportFollowConfig(interactions),
    [interactions]
  );

  if (!viewportTweenControllerRef.current) {
    viewportTweenControllerRef.current = createViewportTweenController();
  }

  const viewportStateRef = useRef<Graph3DViewportState | null>(null);

  const buildViewportState = useCallback((): Graph3DViewportState | null => {
    if (!controls) {
      return null;
    }
    return {
      target: {
        x: controls.target.x,
        y: controls.target.y,
        z: controls.target.z,
      },
      cameraPosition: {
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z,
      },
      distance: camera.position.distanceTo(controls.target),
    };
  }, [camera.position, controls]);

  const syncViewportStateRef = useCallback(() => {
    const nextViewportState = buildViewportState();
    viewportStateRef.current = nextViewportState;
    return nextViewportState;
  }, [buildViewportState]);

  const requestFitView = (options?: Graph3DFitViewOptions) => {
    fitViewTokenRef.current += 1;
    setFitViewRequest({
      token: fitViewTokenRef.current,
      options,
    });
  };

  const requestFocusNode = (
    nodeId: string,
    options?: Graph3DFocusNodeOptions
  ) => {
    if (Date.now() < userViewportInteractionUntilRef.current) {
      return;
    }
    focusNodeTokenRef.current += 1;
    setFocusNodeRequest({
      token: focusNodeTokenRef.current,
      nodeId,
      options,
    });
  };

  const requestViewport = (
    viewport: Graph3DViewportState,
    options?: Graph3DSetViewportOptions
  ) => {
    viewportTokenRef.current += 1;
    setViewportRequest({
      token: viewportTokenRef.current,
      viewport,
      options,
    });
  };

  useImperativeHandle(
    ref,
    () => ({
      fitView: options => {
        requestFitView(options);
      },
      focusNode: (nodeId, options) => {
        requestFocusNode(nodeId, options);
      },
      getViewport: () => viewportStateRef.current,
      setViewport: (viewport, options) => {
        if (!controls) {
          return;
        }
        if ((options?.duration ?? 0) > 0) {
          requestViewport(viewport, options);
          return;
        }
        controls.target.set(
          viewport.target.x,
          viewport.target.y,
          viewport.target.z
        );
        camera.position.set(
          viewport.cameraPosition.x,
          viewport.cameraPosition.y,
          viewport.cameraPosition.z
        );
        camera.lookAt(viewport.target.x, viewport.target.y, viewport.target.z);
        controls.update();
        const nextViewportState = syncViewportStateRef();
        if (nextViewportState) {
          events?.onViewportChange?.(nextViewportState);
        }
      },
    }),
    [
      camera,
      controls,
      events,
      requestFitView,
      requestFocusNode,
      requestViewport,
      syncViewportStateRef,
    ]
  );

  useEffect(
    () => () => {
      viewportTweenControllerRef.current?.stop();
    },
    []
  );

  const fitViewBounds = useMemo(() => {
    if (fitViewRequest?.options?.includeHiddenNodes) {
      return resolved.bounds;
    }
    const visibleNodes = resolved.nodes.filter(
      node =>
        runtime?.nodes?.[node.id]?.hidden !== true &&
        runtime?.nodes?.[node.id]?.culled !== true
    );
    return visibleNodes.length > 0
      ? calculateBounds(visibleNodes)
      : resolved.bounds;
  }, [fitViewRequest, resolved.bounds, resolved.nodes, runtime]);
  const autoFitTargetCenter = useMemo(
    () => ({
      x: centerOffset[0] + resolved.bounds.center.x,
      y: centerOffset[1] + resolved.bounds.center.y,
      z: centerOffset[2] + resolved.bounds.center.z,
    }),
    [
      centerOffset,
      resolved.bounds.center.x,
      resolved.bounds.center.y,
      resolved.bounds.center.z,
    ]
  );
  const fitViewTargetCenter = useMemo(
    () => ({
      x: centerOffset[0] + fitViewBounds.center.x,
      y: centerOffset[1] + fitViewBounds.center.y,
      z: centerOffset[2] + fitViewBounds.center.z,
    }),
    [centerOffset, fitViewBounds]
  );

  const shouldKeepDetailOpen = () =>
    Boolean(
      hoveredNodeIdRef.current === detailNodeIdRef.current ||
        detailPanelHoveredNodeIdRef.current === detailNodeIdRef.current
    );

  const handleSelect = (nodeId: string) => {
    if (interactions?.enableSelection === false) {
      return;
    }
    setSelectedNodeId(nodeId);
    const node = positionedResolved.nodes.find(item => item.id === nodeId) as
      | Graph3DPositionedNode<TNodeData>
      | undefined;
    if (node) {
      events?.onNodeClick?.(node);
    }
  };

  const handleFocus = (nodeId: string) => {
    if (interactions?.enableFocus === false) {
      return;
    }
    setFocusedNodeId(nodeId);
    const node = positionedResolved.nodes.find(item => item.id === nodeId) as
      | Graph3DPositionedNode<TNodeData>
      | undefined;
    if (node) {
      events?.onNodeFocus?.(node);
    }
  };

  const handleDoubleClick = (nodeId: string) => {
    const node = positionedResolved.nodes.find(item => item.id === nodeId) as
      | Graph3DPositionedNode<TNodeData>
      | undefined;
    if (node) {
      events?.onNodeDoubleClick?.({
        ...node,
        resolvedPosition: {
          x: node.resolvedPosition.x + centerOffset[0],
          y: node.resolvedPosition.y + centerOffset[1],
          z: node.resolvedPosition.z + centerOffset[2],
        },
      });
    }
  };

  const createNodeDragEvent = (nodeId: string, position: Graph3DVector3) => {
    const node = positionedResolved.nodes.find(item => item.id === nodeId) as
      | Graph3DPositionedNode<TNodeData>
      | undefined;
    return node
      ? {
          node,
          position,
        }
      : null;
  };

  const handleNodeDragStart = (nodeId: string) => {
    if (interactions?.enableNodeDrag !== true) {
      return;
    }
    const node = positionedResolved.nodes.find(item => item.id === nodeId) as
      | Graph3DPositionedNode<TNodeData>
      | undefined;
    if (node) {
      events?.onNodeDragStart?.({
        node,
        position: node.resolvedPosition,
      });
    }
  };

  const handleNodeDrag = (nodeId: string, position: Graph3DVector3) => {
    if (interactions?.enableNodeDrag !== true) {
      return;
    }
    setNodePositionOverrides(previous => ({
      ...previous,
      [nodeId]: position,
    }));
    const event = createNodeDragEvent(nodeId, position);
    if (event) {
      events?.onNodeDrag?.(event);
    }
  };

  const handleNodeDragEnd = (nodeId: string, position: Graph3DVector3) => {
    if (interactions?.enableNodeDrag !== true) {
      return;
    }
    const event = createNodeDragEvent(nodeId, position);
    if (event) {
      events?.onNodeDragEnd?.(event);
    }
  };

  const handleEdgeClick = (edgeId: string) => {
    const edge = positionedResolved.edges.find(item => item.id === edgeId);
    if (edge) {
      events?.onEdgeClick?.(edge);
    }
  };

  const clearSelectionState = () => {
    setSelectedNodeId(null);
    setFocusedNodeId(null);
  };
  const selectedNode = selectedNodeId
    ? (positionedResolved.nodes.find(item => item.id === selectedNodeId) as
        | Graph3DPositionedNode<TNodeData>
        | undefined)
    : undefined;

  useEffect(() => {
    events?.onSelectionChange?.(selectedNode ?? null);
  }, [events, selectedNode]);

  useEffect(() => {
    if (!controls) {
      return;
    }
    const nextViewportState = syncViewportStateRef();
    if (nextViewportState) {
      events?.onViewportReady?.();
      events?.onViewportChange?.(nextViewportState);
    }
  }, [controls, events, syncViewportStateRef]);

  const cancelDetailHide = () => {
    if (detailHideTimer.current) {
      clearTimeout(detailHideTimer.current);
      detailHideTimer.current = null;
    }
  };

  const scheduleDetailHide = () => {
    cancelDetailHide();
    if (shouldKeepDetailOpen()) {
      return;
    }
    detailHideTimer.current = setTimeout(() => {
      if (shouldKeepDetailOpen()) {
        detailHideTimer.current = null;
        return;
      }
      detailPanelHoveredNodeIdRef.current = null;
      detailNodeIdRef.current = null;
      setDetailNodeId(null);
    }, HOVER_DETAIL_PANEL_HIDE_DELAY);
  };

  const handleHover = (nodeId: string | null) => {
    if (interactions?.enableHover === false) {
      return;
    }
    hoveredNodeIdRef.current = nodeId;
    setHoveredNodeId(nodeId);
    events?.onNodeHover?.(
      nodeId
        ? ((positionedResolved.nodes.find(item => item.id === nodeId) as
            | Graph3DPositionedNode<TNodeData>
            | undefined) ?? null)
        : null
    );
    if (interactions?.enableHoverDetailPanel === false) {
      return;
    }
    if (nodeId) {
      const hoveredNode = positionedResolved.nodes.find(
        item => item.id === nodeId
      );
      if (hoveredNode?.type !== 'result') {
        detailPanelHoveredNodeIdRef.current = null;
        detailNodeIdRef.current = null;
        setDetailNodeId(null);
        return;
      }
      cancelDetailHide();
      if (detailNodeIdRef.current !== nodeId) {
        detailPanelHoveredNodeIdRef.current = null;
      }
      detailNodeIdRef.current = nodeId;
      setDetailNodeId(nodeId);
    } else {
      scheduleDetailHide();
    }
  };

  const detailNode =
    detailNodeId &&
    (resolved.nodes.find(item => item.id === detailNodeId) as
      | Graph3DPositionedNode<TNodeData>
      | undefined);
  const detailContent = detailNodeId
    ? runtime?.nodes?.[detailNodeId]?.detail
    : undefined;
  const renderedEdges = useMemo(
    () =>
      positionedResolved.edges.filter(
        edge => runtime?.edges?.[edge.id]?.culled !== true
      ),
    [positionedResolved.edges, runtime]
  );
  const renderedNodes = useMemo(
    () =>
      positionedResolved.nodes.filter(
        node => runtime?.nodes?.[node.id]?.culled !== true
      ),
    [positionedResolved.nodes, runtime]
  );
  const lastSlowFrameLogAtRef = useRef(0);

  useEffect(() => {
    if (!shouldLogGraph3DPerf()) {
      return;
    }
    console.info(GRAPH3D_SCENE_PERF_PREFIX, 'render-set-changed', {
      renderedNodes: renderedNodes.length,
      totalNodes: resolved.nodes.length,
      culledNodes: resolved.nodes.length - renderedNodes.length,
      renderedEdges: renderedEdges.length,
      totalEdges: resolved.edges.length,
      culledEdges: resolved.edges.length - renderedEdges.length,
    });
  }, [
    renderedEdges.length,
    renderedNodes.length,
    resolved.edges.length,
    resolved.nodes.length,
  ]);

  useFrame((_, delta) => {
    if (!shouldLogGraph3DPerf()) {
      return;
    }
    const deltaMs = delta * 1000;
    if (deltaMs < GRAPH3D_SLOW_FRAME_THRESHOLD_MS) {
      return;
    }
    const now = performance.now();
    if (
      now - lastSlowFrameLogAtRef.current <
      GRAPH3D_SLOW_FRAME_LOG_INTERVAL_MS
    ) {
      return;
    }
    lastSlowFrameLogAtRef.current = now;
    console.info(GRAPH3D_SCENE_PERF_PREFIX, 'slow-frame', {
      deltaMs: Number(deltaMs.toFixed(2)),
      renderedNodes: renderedNodes.length,
      totalNodes: resolved.nodes.length,
      renderedEdges: renderedEdges.length,
      totalEdges: resolved.edges.length,
      ...getGraph3DPerfContext(),
    });
  });

  const markUserViewportInteracting = () => {
    const until = Date.now() + USER_VIEWPORT_GUARD_MS;
    userViewportInteractionUntilRef.current = until;
    setUserViewportInteractionUntil(until);
    viewportTweenControllerRef.current?.stop();
  };

  return (
    <>
      <AutoFitCamera
        bounds={resolved.bounds}
        targetCenter={autoFitTargetCenter}
        enabled={interactions?.enableAutoFit !== false}
      />
      <SceneViewportController
        bounds={fitViewBounds}
        targetCenter={fitViewTargetCenter}
        nodes={resolved.nodes}
        centerOffset={centerOffset}
        viewportTweenControllerRef={viewportTweenControllerRef}
        fitViewRequest={fitViewRequest}
        viewportRequest={viewportRequest}
        focusNodeRequest={focusNodeRequest}
        onFitViewHandled={() => setFitViewRequest(null)}
        onViewportHandled={() => setViewportRequest(null)}
        onFocusNodeHandled={() => setFocusNodeRequest(null)}
        onViewportSettled={events?.onViewportSettled}
      />
      <ambientLight intensity={0.92} />
      <directionalLight position={[260, 280, 460]} intensity={1.45} />
      <pointLight
        position={[-240, -160, 260]}
        intensity={0.42}
        color="#ffffff"
      />
      <group
        position={centerOffset}
        onPointerMissed={event => {
          event.stopPropagation();
          clearSelectionState();
        }}
      >
        {renderedEdges.map(edge => {
          const EdgeRenderer =
            mergedEdgeRenderers[edge.type ?? 'default'] ??
            mergedEdgeRenderers.default;
          return (
            <EdgeRenderer
              key={edge.id}
              edge={edge as Graph3DPositionedEdge<TEdgeData, TNodeData>}
              runtime={runtime?.edges?.[edge.id]}
              theme={theme}
              onClick={handleEdgeClick}
            />
          );
        })}
        {renderedNodes.map(node => {
          const NodeRenderer =
            mergedNodeRenderers[node.type] ?? mergedNodeRenderers.default;
          return (
            <NodeRenderer
              key={node.id}
              node={node as Graph3DPositionedNode<TNodeData>}
              runtime={runtime?.nodes?.[node.id]}
              theme={theme}
              hovered={hoveredNodeId === node.id}
              selected={selectedNodeId === node.id}
              focused={focusedNodeId === node.id}
              hoverEnabled={interactions?.enableHover !== false}
              selectionEnabled={interactions?.enableSelection !== false}
              focusEnabled={interactions?.enableFocus !== false}
              dragEnabled={interactions?.enableNodeDrag === true}
              selectedNodeId={selectedNodeId}
              selectedNode={selectedNode ?? null}
              onHover={handleHover}
              onSelect={handleSelect}
              onFocus={handleFocus}
              onDragStart={handleNodeDragStart}
              onDrag={handleNodeDrag}
              onDragEnd={handleNodeDragEnd}
              onDoubleClick={handleDoubleClick}
              onClearSelection={clearSelectionState}
            />
          );
        })}
        {detailNode && detailContent && (
          <NodeDetailPanel3D
            node={detailNode as Graph3DPositionedNode}
            detail={detailContent}
            onKeepOpen={() => {
              detailPanelHoveredNodeIdRef.current = detailNodeIdRef.current;
              cancelDetailHide();
            }}
            onScheduleClose={() => {
              detailPanelHoveredNodeIdRef.current = null;
              scheduleDetailHide();
            }}
          />
        )}
      </group>
      <OrbitControls
        makeDefault
        enableDamping
        dampingFactor={0.08}
        enablePan={interactions?.enablePan !== false}
        enableZoom={interactions?.enableZoom !== false}
        minDistance={24}
        maxDistance={24000}
        onChange={() => {
          const nextViewportState = syncViewportStateRef();
          if (nextViewportState) {
            events?.onViewportChange?.(nextViewportState);
          }
        }}
        onStart={markUserViewportInteracting}
        onEnd={markUserViewportInteracting}
      />
      <RuntimeViewportTracker
        nodes={positionedResolved.nodes}
        runtime={runtime}
        onFocusNode={requestFocusNode}
        enabled={runtimeViewportFollow.enabled !== false}
        nodeTypes={runtimeViewportFollow.nodeTypes}
        statuses={runtimeViewportFollow.statuses}
        focusPadding={runtimeViewportFollow.padding}
        focusDuration={runtimeViewportFollow.duration}
        suppressAutoFocusUntil={userViewportInteractionUntil}
      />
    </>
  );
}) as <TNodeData = unknown, TEdgeData = unknown>(
  props: Graph3DSceneProps<TNodeData, TEdgeData> & {
    ref?: ForwardedRef<Graph3DHandle>;
  }
) => ReactElement;

export { Graph3DScene };
